/**
 * Per-section sub-screen stub — Phase B placeholder.
 *
 * Renders a "Coming in Phase C" notice with the resolved section
 * + screen identity for QA. Replaced screen-by-screen in Phase C.
 */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Hammer } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SCREEN_NAME_OVERRIDES = {
  'sfg-wms': 'SFG WMS',
  'fg-wms': 'FG WMS',
  'inward-consignment': 'Inward Consignment',
  'shipment-request': 'Shipment Request',
  'internal-transfer': 'Internal Transfers',
  'bundle-receipt': 'Bundle Receipt Entry',
  'bundle-receipt-report': 'Bundle Receipt Report',
  'day-wise-report': 'Day Wise Transaction Report',
  'closing-stock': 'Closing Stock Report',
  'internal-transfer-report': 'Internal Transfer Report',
  'footprint-movement-report': 'Footprint Movement Report',
  'usn-tracking': 'USN Tracking',
  'pieces-processed': 'Pieces Processed',
  'traffic-control': 'Traffic Control',
  'dispatch-supervisor': 'Dispatch Supervisor',
};

export default function PerSectionStub() {
  const navigate = useNavigate();
  const { section: slug, screen } = useParams();
  const screenLabel = SCREEN_NAME_OVERRIDES[screen] || (screen || 'Screen');

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`stub-${slug}-${screen}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/sections/dispatch/${slug}`)}
            className="text-[#F9FAFB] hover:bg-white/10"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">{screenLabel}</h1>
            <p className="text-sm text-[#E8DCC8] capitalize">{slug?.replace('_', ' ')} dispatch section</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white border border-amber-200 rounded-lg p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <Hammer className="h-6 w-6 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-stone-800">{screenLabel} — coming in Phase C</h2>
          <p className="text-sm text-stone-600 mt-2">
            This sub-screen for the <strong className="capitalize">{slug?.replace('_', ' ')}</strong> dispatch
            section is scaffolded but not yet wired to live data. Phase C wires the
            <em> Color Combo</em> flows end-to-end against per-section MongoDB collections
            (<code className="font-mono">{slug}_sfg_inventory</code>,{' '}
            <code className="font-mono">{slug}_fg_inventory</code>, etc.). Phase D + E
            replicate to Art Silk + Kids and finish Combo / ComboV2 / reports.
          </p>
          <div className="mt-6 text-xs text-stone-500 font-mono">
            section_slug = <span className="text-stone-700">{slug}</span> · screen ={' '}
            <span className="text-stone-700">{screen}</span>
          </div>
        </div>
      </main>
    </div>
  );
}
